import React, { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, useTheme } from "@mui/material";
import { Plus } from "lucide-react";
import { DeveloperTrack } from "@/modules/lead-page/new/components/developer-tracks/types";
import NwChip from "@/modules/common/components/ui-kit/chip";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import NextActions from "@/modules/lead-page/new/components/task-management/next-actions";
import LeadTasks from "@/modules/lead-page/new/components/task-management/lead-tasks";
import Recommendations from "@/modules/lead-page/new/components/recommendations/recommendations";
import ActivityTab from "@/modules/lead-page/new/components/activity/activity-tab";
import SalesTab from "@/modules/lead-page/new/components/sales/sales-tab";
import PropertiesTab from "@/modules/lead-page/new/components/properties/properties-tab";
import FullPageModal from "@/modules/common/components/ui-kit/full-page-modal/full-page-modal";
import useActivityTimeline from "@/modules/lead-page/new/hooks/activity-timline.hook";
import MobileFilterMenu from "@/modules/lead-page/new/components/activity/filters/mobile-filter-menu";
import AddComment from "@/modules/lead-page/new/components/activity/add-comment";
import InterestsTab from "@/modules/lead-page/new/components/lead-interests";
import ReviewsTab from "@/modules/lead-page/new/components/ratings";
import DeveloperTracksTab from "@/modules/lead-page/new/components/developer-tracks";
import MobileRatingFilterMenu from "@/modules/lead-page/new/components/ratings/filters/mobile-rating-filter-menu";
import { useRatingFilter } from "@/modules/lead-page/new/hooks/ratings/use-rating-filter.hook";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import CreateTrackModal from "../developer-tracks/modals/create-track-modal";
import { useLeadTracks } from "../developer-tracks/hooks/use-lead-tracks.hook";
import {
  EnvironmentVariables,
  OrganizationTierTypes,
} from "@/modules/common/types";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";

interface MobileBottomSheetsProps {
  isTasksOpen: boolean;
  isNextActionsOpen: boolean;
  isRecommendationsOpen: boolean;
  isActivitiesOpen: boolean;
  isSalesOpen: boolean;
  isPropertiesOpen: boolean;
  onTasksClose: () => void;
  onNextActionsClose: () => void;
  onRecommendationsClose: () => void;
  onActivitiesClose: () => void;
  onSalesClose: () => void;
  onPropertiesClose: () => void;
  // Interests
  isInterestsOpen: boolean;
  onInterestsClose: () => void;
  // Developer Tracks
  isDeveloperTracksOpen: boolean;
  onDeveloperTracksClose: () => void;
  // Ratings
  isRatingsOpen: boolean;
  onRatingsClose: () => void;
  // Navigation
  onActivitiesClick: () => void;
  onDeveloperTracksClick: () => void;
}

const MobileBottomSheets: React.FC<MobileBottomSheetsProps> = ({
  isTasksOpen,
  isNextActionsOpen,
  isRecommendationsOpen,
  isActivitiesOpen,
  isSalesOpen,
  isPropertiesOpen,
  onTasksClose,
  onNextActionsClose,
  onRecommendationsClose,
  onActivitiesClose,
  onSalesClose,
  onPropertiesClose,
  isInterestsOpen,
  onInterestsClose,
  isDeveloperTracksOpen,
  onDeveloperTracksClose,
  isRatingsOpen,
  onRatingsClose,
  onActivitiesClick,
  onDeveloperTracksClick,
}) => {
  const theme = useTheme();
  const { isLoading, isFetching, count } = useActivityTimeline();
  const {
    ratingFilterHookResult,
    ratingValue,
    sortOrder,
    count: ratingCount,
  } = useRatingFilter();

  // State for selected developer track on mobile
  const [selectedMobileTrack, setSelectedMobileTrack] =
    useState<DeveloperTrack | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [cameFromActivity, setCameFromActivity] = useState(false);

  const handleMobileTrackSelect = (track: DeveloperTrack) => {
    setSelectedMobileTrack(track);
  };

  const handleBackToTracks = () => {
    setSelectedMobileTrack(null);
  };

  // Reset selected track when modal closes
  const handleDeveloperTracksModalClose = () => {
    setSelectedMobileTrack(null);
    if (cameFromActivity) {
      setCameFromActivity(false);
      onActivitiesClick();
    }
    onDeveloperTracksClose();
  };

  const handleFindInActivity = () => {
    onRatingsClose();
    onActivitiesClick();
  };

  // Navigate from activity track card to developer tracks modal
  const [searchParams, setSearchParams] = useSearchParams();
  const handleNavigateToTrack = (trackUId: string) => {
    searchParams.set("trackUId", trackUId);
    setSearchParams(searchParams, { replace: true });
    onActivitiesClose();
    setCameFromActivity(true);
    onDeveloperTracksClick();
  };
  const { lead } = useContext(LeadPageContext);
  const hasAssignedAgent = !!lead?.data?.lead?.user?.id;

  // Generate the title for Developer Tracks modal
  const getDeveloperTracksTitle = () => {
    if (selectedMobileTrack) {
      const backgroundColor =
        selectedMobileTrack.currentStatusColor || "#F5F5F5";
      const textColor = "#313131";

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            flexWrap: "wrap",
          }}
        >
          <span>{selectedMobileTrack.developerName} track</span>
          <NwChip
            label={selectedMobileTrack.currentStatus}
            size="small"
            sx={{
              backgroundColor,
              color: textColor,
              height: "24px",
              "& .MuiChip-label": {
                padding: `0 ${theme.nawy.spacing.xs}`,
                ...theme.nawy.typography.english.variants.body.sm.regular,
              },
            }}
          />
        </Box>
      );
    }
    return "Developer Tracks";
  };

  // Fetch tracks to determine if we should show the header button
  const { data: tracksData } = useLeadTracks({
    leadUId: lead?.data?.lead?.uid || "",
    enabled: !!lead?.data?.lead?.uid,
  });
  
  const hasTracks = (tracksData?.results?.length || 0) > 0;
  
  const leadType = lead?.data?.lead?.leadType as any;
  const hasDeveloperTracks = leadType?.has_developer_tracks ?? leadType?.hasDeveloperTracks ?? false;
  const canAddTrack = !lead?.data?.lead?.archived && hasDeveloperTracks;

  return (
    <>
      {EnvironmentVariables.COMPANY_NAME.toString() ===
        OrganizationTierTypes.NAWY &&
        hasAssignedAgent && (
          <CommonBottomSheet isOpen={isTasksOpen} onClose={onTasksClose}>
            <LeadTasks />
          </CommonBottomSheet>
        )}

      <CommonBottomSheet
        isOpen={isNextActionsOpen}
        onClose={onNextActionsClose}
      >
        <NextActions />
      </CommonBottomSheet>

      <CommonBottomSheet
        isOpen={isRecommendationsOpen}
        onClose={onRecommendationsClose}
      >
        <Recommendations />
      </CommonBottomSheet>

      {/* Full Page Modal for Activities */}
      <FullPageModal
        isOpen={isActivitiesOpen}
        onClose={onActivitiesClose}
        title={`Activity (${isLoading || isFetching ? "..." : count})`}
        extraButtons={<MobileFilterMenu />}
      >
        <ActivityTab
          isHeaderCollapsed={false}
          onCollapseChange={undefined}
          addComment={<AddComment />}
          onNavigateToTrack={handleNavigateToTrack}
        />
      </FullPageModal>

      {/* Full Page Modal for Sales */}
      <FullPageModal isOpen={isSalesOpen} onClose={onSalesClose} title="Sales">
        <SalesTab />
      </FullPageModal>

      {/* Full Page Modal for Properties */}
      <FullPageModal
        isOpen={isPropertiesOpen}
        onClose={onPropertiesClose}
        title="Properties"
      >
        <PropertiesTab />
      </FullPageModal>

      <FullPageModal
        isOpen={isInterestsOpen}
        onClose={onInterestsClose}
        title="Interests"
      >
        <InterestsTab />
      </FullPageModal>

      {/* Full Page Modal for Developer Tracks */}
      <FullPageModal
        isOpen={isDeveloperTracksOpen}
        onClose={
          selectedMobileTrack
            ? handleBackToTracks
            : handleDeveloperTracksModalClose
        }
        title={getDeveloperTracksTitle()}
        extraButtons={
          !selectedMobileTrack && canAddTrack && hasTracks ? (
            <Button
              variant="contained"
              onClick={() => setIsCreateModalOpen(true)}
              sx={{
                backgroundColor: theme.nawy.colors.semantic.layer.accent,
                color: theme.nawy.colors.semantic.text.onColor,
                borderRadius: theme.nawy.radius.lg,
                textTransform: "none",
                height: "36px",
                width: "36px",
                minWidth: "36px",
                padding: 0,
                boxShadow: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                  backgroundColor: theme.nawy.colors.semantic.layer.accentDark,
                  boxShadow: "none",
                },
              }}
            >
              <Plus size={16} />
            </Button>
          ) : undefined
        }
      >
        <DeveloperTracksTab
          onMobileTrackSelect={handleMobileTrackSelect}
          selectedMobileTrack={selectedMobileTrack}
        />
      </FullPageModal>

      {/* Full Page Modal for Ratings */}
      <FullPageModal
        isOpen={isRatingsOpen}
        onClose={onRatingsClose}
        title={
          <span>
            Ratings
            <span
              style={{
                ...nawyTheme.nawy.typography.english.variants.body.sm.regular,
                color: nawyTheme.nawy.colors.semantic.text.secondary,
              }}
            >
              {` (${ratingCount})`}
            </span>
          </span>
        }
        extraButtons={
          <MobileRatingFilterMenu
            ratingFilterHookResult={ratingFilterHookResult}
          />
        }
      >
        <ReviewsTab
          ratingFilter={ratingValue}
          sortOrder={sortOrder}
          onTabChange={handleFindInActivity}
        />
      </FullPageModal>

      <CreateTrackModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </>
  );
};

export default MobileBottomSheets;
